import * as Sentry from '@sentry/nextjs'

import { getPublicPrices } from '@/lib/payment-service'
import {
  TRIAL_INTERVAL_COUNT_METADATA_KEY,
  TRIAL_INTERVAL_METADATA_KEY,
  TRIAL_RENEWAL_LOOKUP_KEY_METADATA_KEY,
} from '@/lib/prices/priceUtils'

import { type Price, PriceProductType } from '@/clients/payment-service'

import type { IntentCheckoutLine } from './types'

/** The product details of a price, loaded at build time; the quote supplies every amount the checkout shows. */
export type IntentPrice = Pick<
  Price,
  | 'lookup_key'
  | 'nickname'
  | 'unit_amount'
  | 'product_type'
  | 'product_name'
  | 'product_image'
  | 'recurring'
  | 'intro_offer_type'
  | 'metadata'
>

const TRIAL_METADATA_KEYS = [
  TRIAL_INTERVAL_METADATA_KEY,
  TRIAL_INTERVAL_COUNT_METADATA_KEY,
  TRIAL_RENEWAL_LOOKUP_KEY_METADATA_KEY,
]

// Nulls rather than undefined: Next cannot serialise undefined page props.
export const toIntentPrice = (price: Price): IntentPrice => ({
  lookup_key: price.lookup_key,
  nickname: price.nickname,
  unit_amount: price.unit_amount,
  product_type: price.product_type,
  product_name: price.product_name ?? null,
  product_image: price.product_image ?? null,
  recurring: price.recurring ?? null,
  intro_offer_type: price.intro_offer_type ?? null,
  metadata: Object.fromEntries(
    Object.entries(price.metadata ?? {}).filter(([key]) => TRIAL_METADATA_KEYS.includes(key)),
  ),
})

const { ZOE_PLUS, GUT_MICROBIOME_TEST, DAILY30, BARS, SHIPPING_FEE, ZOE_LUNCHBOX, ZOE_TRAVEL_POUCH } = PriceProductType

/** The free gifts payment-service adds to a basket, such as the Daily30 4-month plan's regional gift. */
const GIFT_PRODUCT_TYPES: PriceProductType[] = [ZOE_LUNCHBOX, ZOE_TRAVEL_POUCH]

/** What an intent basket can hold on each product's checkout and confirmation pages: the product, upsells, gifts and shipping. */
export const ZOE_PLUS_INTENT_PRODUCT_TYPES = [ZOE_PLUS, GUT_MICROBIOME_TEST, SHIPPING_FEE]
export const GUT_HEALTH_TEST_INTENT_PRODUCT_TYPES = [GUT_MICROBIOME_TEST, SHIPPING_FEE]
export const DAILY30_INTENT_PRODUCT_TYPES = [DAILY30, BARS, ...GIFT_PRODUCT_TYPES, SHIPPING_FEE]
export const BARS_INTENT_PRODUCT_TYPES = [BARS, SHIPPING_FEE]

/**
 * The market's live prices of the given product types for a page's `getStaticProps`. Hidden prices are included:
 * experiment arms and trial renewals are not listed.
 */
export const getIntentCheckoutPrices = async (
  countryCode: string,
  productTypes: PriceProductType[],
): Promise<IntentPrice[]> => {
  const [visible, hidden] = await Promise.all([
    getPublicPrices(countryCode, { visible: true, live: true }),
    getPublicPrices(countryCode, { visible: false, live: true }),
  ])
  return [...visible, ...hidden].filter((price) => productTypes.includes(price.product_type)).map(toIntentPrice)
}

const reportedMissingLookupKeys = new Set<string>()

/** The price for a line's lookup key; one missing from the build-time prices is reported to Sentry once. */
export const getIntentPrice = (prices: IntentPrice[], lookupKey: string): IntentPrice | undefined => {
  const price = prices.find((candidate) => candidate.lookup_key === lookupKey)
  if (!price && !reportedMissingLookupKeys.has(lookupKey)) {
    reportedMissingLookupKeys.add(lookupKey)
    Sentry.captureException(new Error('Intent checkout line is not in the build-time prices'), {
      level: 'warning',
      extra: { lookupKey },
    })
  }
  return price
}

/** The lines with free gifts after the paid lines, as the session checkout lists them; the order is otherwise kept. */
export const withGiftsLast = (lines: IntentCheckoutLine[], prices: IntentPrice[]): IntentCheckoutLine[] => {
  const isGift = (line: IntentCheckoutLine) => {
    const productType = prices.find((price) => price.lookup_key === line.price_lookup_key)?.product_type
    return productType !== undefined && GIFT_PRODUCT_TYPES.includes(productType)
  }
  return [...lines].sort((a, b) => Number(isGift(a)) - Number(isGift(b)))
}
